import type { ReactNode } from 'react';
import { CircularProgress, RoleBadge } from '@cere/cere-design-system';
import type { OrgSummary } from '@/lib/clients/vault/orgs';

interface Props {
  /** The session's orgs list — owner entry first (fetchOrgs contract). */
  orgs: OrgSummary[];
  /** False until the boot orgs fetch resolves; undefined treated as loaded
   *  (same contract as `VaultSession.agentsLoaded`). */
  loaded?: boolean;
  /** True when the boot orgs fetch FAILED. The layout already surfaces the
   *  error via the shared snackbar; the switcher needs the flag so it does
   *  not assert "you don't belong to any organization" about a list it never
   *  received — a failed read is not an empty result. */
  failed?: boolean;
  /** Which vault the Organizations section is showing; null = My vault. */
  activeOrgVaultId: string | null;
  onSelect: (vaultId: string | null) => void;
  /** Fallback caption for the My-vault row when no owner entry exists. */
  myVaultName: string;
}

/**
 * The vault-context switcher: "My vault" plus one row per organization the
 * wallet belongs to. The owner entry in `orgs` IS the My-vault row (rendered
 * as such, not duplicated); only non-owner entries become org rows.
 */
export function OrgSwitcher({ orgs, loaded, failed, activeOrgVaultId, onSelect, myVaultName }: Props) {
  if (loaded === false) {
    // Loader, not an empty list: "still fetching" must not read as "you
    // belong to nothing" — the boot roundtrip is one Bearer GET away.
    return (
      <div className="vault-orgs__switch-loading">
        <CircularProgress size={20} />
      </div>
    );
  }
  const owner = orgs.find((o) => o.isOwner) ?? null;
  const joined = orgs.filter((o) => !o.isOwner);
  return (
    <div className="vault-orgs__switch">
      <SwitchRow
        active={activeOrgVaultId === null}
        label="My vault"
        caption={owner?.name ?? myVaultName}
        // Badged on the owner entry's PRESENCE, not the row's existence: with
        // no owner entry the boot join observed no own vault, and a row that
        // claims ownership of an unobserved vault lies about the wallet.
        badge={owner ? <RoleBadge label="Owner" /> : null}
        onClick={() => onSelect(null)}
      />
      {joined.map((o) => (
        <SwitchRow
          key={o.vaultId}
          active={activeOrgVaultId === o.vaultId}
          label={o.name}
          caption={scopeCountLabel(o)}
          badge={<RoleBadge label={o.role === 'admin' ? 'Admin' : 'Member'} />}
          onClick={() => onSelect(o.vaultId)}
        />
      ))}
      {joined.length === 0 && (failed
        ? <p className="vault-orgs__switch-empty" role="status">We couldn't load your organizations. They may still be there — try again in a moment.</p>
        : <p className="vault-orgs__switch-empty">You don't belong to any organization yet.</p>
      )}
    </div>
  );
}

function scopeCountLabel(o: OrgSummary): string {
  const n = o.scopes.length;
  return `${n} ${n === 1 ? 'scope' : 'scopes'}`;
}

function SwitchRow({
  active, label, caption, badge, onClick,
}: {
  active: boolean;
  label: string;
  caption?: string;
  badge: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`vault-orgs__row${active ? ' vault-orgs__row--active' : ''}`}
      aria-current={active ? 'true' : undefined}
      onClick={onClick}
    >
      <span className="vault-orgs__row-text">
        <span className="vault-orgs__row-label">{label}</span>
        {caption && <span className="vault-orgs__row-caption">{caption}</span>}
      </span>
      {badge}
    </button>
  );
}
